import { VariablesOf } from "@graphql-typed-document-node/core";
import {
  BookingChangeRequestListItem,
  bookingChangeRequestListQuery,
} from "@/app/Bookings/GraphQL/bookingChangeRequestListQuery";
import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import { useBookingChangeRequestActions } from "@/app/Bookings/Hooks/useBookingChangeRequestActions";
import type { TableId } from "@/app/Common/Utils/table-id";

import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { bookingChangeRequestStatusColorMap } from "@/app/Bookings/Utils/bookingChangeRequestStatusColorMap";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { CellContext } from "@/lib/Components/DataTable/types";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingChangeRequestApproveDialog } from "@/app/Bookings/Components/Dialogs/BookingChangeRequestApproveDialog";
import { BookingChangeRequestActionedDialog } from "@/app/Bookings/Components/Dialogs/BookingChangeRequestActionedDialog";
import { BookingChangeRequestStatus } from "@/gql/graphql";
import { ReactNode } from "react";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

type BookingChangeRequestColumnId =
  | "id"
  | "status"
  | "amount_paid"
  | "request"
  | "created_at"
  | "booking"
  | "supplier"
  | "actions";
export function ChangeRequestTable({
  queryVariables,
  id,
  rightButtons,
}: {
  id: TableId;
  queryVariables?: Partial<VariablesOf<typeof bookingChangeRequestListQuery>>;
  rightButtons?: ReactNode;
}) {
  const t = useTranslations("bookingChangeRequest");
  const getActions = useBookingChangeRequestActions();

  const columns: DataTableColDef<
    BookingChangeRequestListItem,
    any,
    BookingChangeRequestColumnId
  >[] = [
    {
      id: "booking",
      header: "Booking",
      size: 100,
      cell: ({ row }) => (
        <ModelLinkCell
          to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
          params={(prev) => ({ ...prev, bookingId: row.original.booking.id })}
          viewTransition
        >
          {row.original.booking.reference}
        </ModelLinkCell>
      ),
    },
    {
      id: "supplier",
      header: "Supplier",
      size: 100,
      cell: ({ row }) => (
        <div className="text-gray-500">
          {row.original.booking.supplier.name}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      size: 50,
      cell: ({ row }) => (
        <Badge
          color={bookingChangeRequestStatusColorMap[row.original.status]}
          className="capitalize"
        >
          {t(`status.${row.original.status}`)}
        </Badge>
      ),
    },
    {
      id: "amount_paid",
      header: "Paid",
      accessorFn: (row) =>
        row.stripePayment
          ? formatCurrency(row.stripePayment.amount, row.stripePayment.currency)
          : "--",
    },
    {
      id: "request",
      header: "Request",
      cell: ({ row }) => {
        return <div className="text-gray-500">{row.original.request}</div>;
      },
    },
    {
      id: "created_at",
      header: "Created",
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "actions",
      cell: ChangeRequestActionCell,
    },
  ];

  return (
    <DataTable
      id={id}
      title="Change requests"
      searchable={false}
      document={bookingChangeRequestListQuery}
      columnToggles={false}
      columns={columns}
      getActions={getActions}
      rightButtons={rightButtons}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          ...queryVariables,
        };
      }}
      accessor={(data) => data.bookingChangeRequests}
    />
  );
}

function ChangeRequestActionCell({
  row,
}: CellContext<BookingChangeRequestListItem, any>) {
  const { open } = useDialog(BookingChangeRequestApproveDialog);
  const { open: openActionDialog } = useDialog(
    BookingChangeRequestActionedDialog,
  );

  return (
    <div className="flex justify-end gap-2">
      {row.original.status === BookingChangeRequestStatus.Pending ? (
        <Button
          color="sky"
          onClick={() => {
            openActionDialog({
              changeRequest: row.original,
            });
          }}
        >
          Actioned
        </Button>
      ) : null}
      <Button
        color="green"
        onClick={() => {
          open({
            changeRequest: row.original,
            type: "approve",
          });
        }}
      >
        Accept
      </Button>
      <Button
        color="rose"
        onClick={() => {
          open({
            changeRequest: row.original,
            type: "reject",
          });
        }}
      >
        Decline
      </Button>
    </div>
  );
}
